import styles from './TermsPage.module.css';

export default function PrivacyPage() {
  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <h1>Privacy Policy</h1>
        <p className={styles.lastUpdated}>Last Updated: December 14, 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Rad SBC ("we," "our," or "us") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store, and protect your information when you use our browser extension and related services (the "Service").
          </p>
          <p>
            This Privacy Policy complies with the EU General Data Protection Regulation (GDPR) and the Polish Personal Data Protection Act (implementing GDPR in Poland).
          </p>
          <p>
            <strong>Data Controller:</strong> Rad SBC, operating from Poland, is the data controller responsible for your personal data.
          </p>
        </section>

        <section>
          <h2>2. Legal Basis for Processing</h2>
          <p>We process your personal data based on the following legal grounds:</p>
          <ul>
            <li><strong>Consent (Article 6(1)(a) GDPR):</strong> You provide explicit consent when you install and use our Service</li>
            <li><strong>Legitimate Interest (Article 6(1)(f) GDPR):</strong> To improve our Service, prevent fraud, and ensure security</li>
            <li><strong>Contract Performance (Article 6(1)(b) GDPR):</strong> To provide the Service you requested</li>
          </ul>
        </section>

        <section>
          <h2>3. What Information We Collect</h2>

          <h3>3.1 Authentication Data</h3>
          <p>When you authenticate with our Service via Kinde:</p>
          <ul>
            <li>Email address</li>
            <li>Name (if provided)</li>
            <li>Profile picture (if provided)</li>
            <li>Unique user identifier</li>
            <li>Authentication tokens</li>
          </ul>

          <h3>3.2 Usage Data</h3>
          <p>We may collect anonymous, aggregated usage data:</p>
          <ul>
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>Extension version</li>
            <li>Feature usage statistics (anonymized)</li>
            <li>Error logs and crash reports (anonymized)</li>
          </ul>

          <h3>3.3 Data We Do NOT Collect</h3>
          <p><strong>Important:</strong> We do NOT collect, store, or have access to:</p>
          <ul>
            <li>Your EA account credentials (username or password)</li>
            <li>Your EA FC game data or player inventory</li>
            <li>Your browsing history outside of EA FC Web App</li>
            <li>Your IP address (stored only temporarily by our hosting provider)</li>
            <li>Your precise geolocation</li>
            <li>Any payment or financial information</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Use Your Information</h2>
          <p>We use your information for the following purposes:</p>
          <ul>
            <li><strong>Service Provision:</strong> To authenticate users and provide SBC solving functionality</li>
            <li><strong>Service Improvement:</strong> To analyze usage patterns and improve features (using anonymized data)</li>
            <li><strong>Communication:</strong> To send important service updates or security notifications (if you opted in)</li>
            <li><strong>Security:</strong> To detect and prevent fraud, abuse, or security incidents</li>
            <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Storage and Security</h2>

          <h3>5.1 Where We Store Data</h3>
          <ul>
            <li><strong>Authentication Data:</strong> Stored by Kinde (our authentication provider) with servers in the EU/EEA</li>
            <li><strong>Application Data:</strong> Stored on secure servers within the EU/EEA</li>
            <li><strong>Local Storage:</strong> Some data is stored locally in your browser (you control this data)</li>
          </ul>

          <h3>5.2 Security Measures</h3>
          <p>We implement appropriate technical and organizational measures to protect your data:</p>
          <ul>
            <li>Encryption in transit (HTTPS/TLS)</li>
            <li>Encryption at rest for sensitive data</li>
            <li>Access controls and authentication</li>
            <li>Regular security audits and updates</li>
            <li>Minimal data collection principle</li>
          </ul>

          <h3>5.3 Data Retention</h3>
          <ul>
            <li><strong>Account Data:</strong> Retained while your account is active</li>
            <li><strong>Usage Data:</strong> Anonymized usage data may be retained for up to 24 months for analytics</li>
            <li><strong>Deletion:</strong> Upon account deletion, your personal data is permanently deleted within 30 days</li>
          </ul>
        </section>

        <section>
          <h2>6. Third-Party Services</h2>

          <h3>6.1 Kinde (Authentication Provider)</h3>
          <p>
            We use Kinde for authentication services. Kinde processes your authentication data in accordance with their privacy policy and GDPR compliance.
            Learn more at: <a href="https://kinde.com/privacy" target="_blank" rel="noopener noreferrer">https://kinde.com/privacy</a>
          </p>

          <h3>6.2 Data Transfers</h3>
          <p>
            We ensure that any data transfers to third parties comply with GDPR requirements:
          </p>
          <ul>
            <li>We only work with GDPR-compliant service providers</li>
            <li>Data Processing Agreements (DPAs) are in place with all processors</li>
            <li>We use Standard Contractual Clauses (SCCs) where applicable</li>
            <li>No data is transferred outside the EU/EEA without appropriate safeguards</li>
          </ul>
        </section>

        <section>
          <h2>7. Your Rights Under GDPR</h2>
          <p>As a data subject in the EU, you have the following rights:</p>

          <h3>7.1 Right of Access (Article 15 GDPR)</h3>
          <p>You have the right to request a copy of the personal data we hold about you.</p>

          <h3>7.2 Right to Rectification (Article 16 GDPR)</h3>
          <p>You have the right to correct inaccurate or incomplete personal data.</p>

          <h3>7.3 Right to Erasure / "Right to be Forgotten" (Article 17 GDPR)</h3>
          <p>You have the right to request deletion of your personal data when:</p>
          <ul>
            <li>The data is no longer necessary for the purposes it was collected</li>
            <li>You withdraw consent and there is no other legal basis for processing</li>
            <li>You object to processing and there are no overriding legitimate grounds</li>
            <li>The data has been unlawfully processed</li>
          </ul>

          <h3>7.4 Right to Restriction of Processing (Article 18 GDPR)</h3>
          <p>You have the right to request restriction of processing in certain circumstances.</p>

          <h3>7.5 Right to Data Portability (Article 20 GDPR)</h3>
          <p>You have the right to receive your personal data in a structured, commonly used, and machine-readable format.</p>

          <h3>7.6 Right to Object (Article 21 GDPR)</h3>
          <p>You have the right to object to processing based on legitimate interests or for direct marketing purposes.</p>

          <h3>7.7 Right to Withdraw Consent (Article 7(3) GDPR)</h3>
          <p>You can withdraw your consent at any time by uninstalling the extension or deleting your account.</p>

          <h3>7.8 Right to Lodge a Complaint</h3>
          <p>
            You have the right to lodge a complaint with a supervisory authority:
          </p>
          <ul>
            <li><strong>Poland:</strong> Urząd Ochrony Danych Osobowych (UODO) - <a href="https://uodo.gov.pl" target="_blank" rel="noopener noreferrer">https://uodo.gov.pl</a></li>
            <li><strong>EU:</strong> Your local data protection authority</li>
          </ul>

          <h3>7.9 How to Exercise Your Rights</h3>
          <p>
            To exercise any of these rights, please contact us at: <a href="mailto:privacy@radx.pl">privacy@radx.pl</a>
          </p>
          <p>We will respond to your request within one month (extendable by two months for complex requests).</p>
        </section>

        <section>
          <h2>8. Cookies and Similar Technologies</h2>

          <h3>8.1 What We Use</h3>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for authentication and core functionality</li>
            <li><strong>Local Storage:</strong> To store user preferences and settings locally in your browser</li>
          </ul>

          <h3>8.2 What We Don't Use</h3>
          <p>We do NOT use:</p>
          <ul>
            <li>Advertising cookies or tracking pixels</li>
            <li>Third-party analytics cookies (e.g., Google Analytics)</li>
            <li>Social media tracking cookies</li>
          </ul>

          <h3>8.3 Managing Cookies</h3>
          <p>
            You can control cookies through your browser settings. Note that disabling essential cookies may affect Service functionality.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Our Service is not intended for children under 16 years of age. We do not knowingly collect personal data from children under 16.
          </p>
          <p>
            If you are a parent or guardian and believe your child has provided us with personal data, please contact us at <a href="mailto:privacy@radx.pl">privacy@radx.pl</a> and we will delete such information.
          </p>
        </section>

        <section>
          <h2>10. Data Breach Notification</h2>
          <p>
            In the event of a data breach that is likely to result in a risk to your rights and freedoms, we will:
          </p>
          <ul>
            <li>Notify the relevant supervisory authority within 72 hours (as required by GDPR)</li>
            <li>Notify affected users without undue delay if the breach poses a high risk</li>
            <li>Provide information about the nature of the breach and measures taken</li>
          </ul>
        </section>

        <section>
          <h2>11. Automated Decision-Making and Profiling</h2>
          <p>
            <strong>We do NOT engage in automated decision-making or profiling</strong> that produces legal effects or similarly significantly affects you.
          </p>
          <p>
            Our Service uses algorithms to solve SBCs, but this is a tool you use voluntarily and does not constitute automated decision-making under GDPR.
          </p>
        </section>

        <section>
          <h2>12. International Data Transfers</h2>
          <p>
            We process data primarily within the EU/EEA. If any data is transferred outside the EU/EEA, we ensure appropriate safeguards are in place:
          </p>
          <ul>
            <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
            <li>Adequacy decisions by the European Commission</li>
            <li>Binding Corporate Rules (where applicable)</li>
          </ul>
        </section>

        <section>
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we make material changes:
          </p>
          <ul>
            <li>We will update the "Last Updated" date at the top of this page</li>
            <li>We will notify you through the Service or via email (if applicable)</li>
            <li>For material changes affecting your rights, we may request renewed consent</li>
          </ul>
          <p>
            We encourage you to review this Privacy Policy periodically.
          </p>
        </section>

        <section>
          <h2>14. Contact Information</h2>

          <h3>14.1 Data Controller</h3>
          <p>
            <strong>Rad SBC</strong><br />
            Email: <a href="mailto:privacy@radx.pl">privacy@radx.pl</a><br />
            General Contact: <a href="mailto:support@radx.pl">support@radx.pl</a>
          </p>

          <h3>14.2 Data Protection Officer (DPO)</h3>
          <p>
            For data protection inquiries, contact: <a href="mailto:dpo@radx.pl">dpo@radx.pl</a>
          </p>

          <h3>14.3 Supervisory Authority</h3>
          <p>
            <strong>Poland (UODO):</strong><br />
            Urząd Ochrony Danych Osobowych<br />
            ul. Stawki 2, 00-193 Warszawa<br />
            Website: <a href="https://uodo.gov.pl" target="_blank" rel="noopener noreferrer">https://uodo.gov.pl</a>
          </p>
        </section>

        <section className={styles.acknowledgment}>
          <h2>15. Consent</h2>
          <p>
            BY USING OUR SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THIS PRIVACY POLICY AND CONSENT TO THE COLLECTION, USE, AND PROCESSING OF YOUR PERSONAL DATA AS DESCRIBED HEREIN.
          </p>
          <p>
            You may withdraw your consent at any time by discontinuing use of the Service and requesting deletion of your account.
          </p>
        </section>

        <section>
          <h2>16. Cookie Declaration</h2>
          <p>
            Below is a detailed list of all cookies used on our website. This declaration is automatically updated by Cookiebot.
          </p>
          <script
            id="CookieDeclaration"
            src="https://consent.cookiebot.com/68aad65f-c263-4291-88f6-a8bda025e053/cd.js"
            type="text/javascript"
            async
          ></script>
        </section>
      </div>
    </main>
  );
}
